import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, resolveDynamicComponent as _resolveDynamicComponent, normalizeClass as _normalizeClass, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "flex items-center gap-2 mb-4" }
const _hoisted_2 = {
  key: 0,
  class: "text-center py-10"
}
const _hoisted_3 = { class: "h-14 w-14 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4" }
const _hoisted_4 = { class: "text-lg font-semibold mb-2" }
const _hoisted_5 = { class: "text-muted-foreground text-sm mb-6" }
const _hoisted_6 = { class: "flex items-center justify-center gap-3" }
const _hoisted_7 = { class: "space-y-3" }
const _hoisted_8 = { class: "font-medium text-sm" }
const _hoisted_9 = { class: "text-xs text-muted-foreground" }
const _hoisted_10 = {
  key: 0,
  class: "space-y-3"
}
const _hoisted_11 = { class: "flex flex-col items-center gap-3 py-3" }
const _hoisted_12 = { class: "flex gap-1" }
const _hoisted_13 = ["onClick", "onMouseenter"]
const _hoisted_14 = { class: "space-y-2" }
const _hoisted_15 = {
  key: 1,
  class: "space-y-2"
}
const _hoisted_16 = {
  key: 2,
  class: "space-y-2"
}
const _hoisted_17 = {
  key: 0,
  class: "space-y-3 py-4"
}
const _hoisted_18 = {
  key: 1,
  class: "text-center py-12"
}
const _hoisted_19 = {
  key: 2,
  class: "space-y-3"
}
const _hoisted_20 = { class: "flex items-center gap-4 text-xs text-muted-foreground pb-2" }
const _hoisted_21 = {
  key: 0,
  class: "text-primary font-medium"
}
const _hoisted_22 = {
  key: 1,
  class: "text-emerald-500 font-medium"
}
const _hoisted_23 = ["onClick"]
const _hoisted_24 = { class: "flex-1 min-w-0" }
const _hoisted_25 = { class: "flex items-center gap-2 mb-0.5" }
const _hoisted_26 = { class: "text-sm font-medium truncate" }
const _hoisted_27 = { class: "flex items-center gap-2" }
const _hoisted_28 = { class: "text-[11px] text-muted-foreground" }
const _hoisted_29 = {
  key: 0,
  class: "flex items-center gap-1 text-[11px] text-emerald-500 font-medium"
}
const _hoisted_30 = {
  key: 0,
  class: "px-3 pb-3 space-y-3"
}
const _hoisted_31 = {
  key: 0,
  class: "flex items-center gap-2"
}
const _hoisted_32 = { class: "text-lg tracking-wider text-yellow-500" }
const _hoisted_33 = { class: "text-xs text-muted-foreground" }
const _hoisted_34 = { key: 1 }
const _hoisted_35 = { class: "text-sm whitespace-pre-wrap bg-muted/50 rounded-lg p-3" }
const _hoisted_36 = {
  key: 2,
  class: "text-[11px] text-muted-foreground"
}
const _hoisted_37 = { class: "font-mono" }
const _hoisted_38 = {
  key: 3,
  class: "mt-2"
}
const _hoisted_39 = { class: "rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3" }
const _hoisted_40 = { class: "text-sm whitespace-pre-wrap" }
const _hoisted_41 = {
  key: 4,
  class: "space-y-2"
}
const _hoisted_42 = { class: "text-xs text-muted-foreground font-medium" }
const _hoisted_43 = { class: "flex items-center justify-between mb-1.5" }
const _hoisted_44 = { class: "text-xs font-medium text-emerald-600 dark:text-emerald-400" }
const _hoisted_45 = { class: "text-[10px] text-muted-foreground" }
const _hoisted_46 = { class: "text-sm whitespace-pre-wrap" }
const _hoisted_47 = {
  key: 5,
  class: "flex items-center gap-2 text-xs text-muted-foreground py-1"
}

import { ref, computed, onMounted } from 'vue'
import { collection, addDoc, serverTimestamp, query, where, orderBy, getDocs } from '@/lib/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/stores/auth'
import { useRoute } from 'vue-router'
import { useToast } from '@/components/ui/toast'
import { formatDate, formatDateTime } from '@/utils/formatters'
import type { FeedbackType, UserFeedback } from '@/types/help'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import {
  MessageSquare,
  Bug,
  Lightbulb,
  TrendingUp,
  HelpCircle,
  Loader2,
  CheckCircle,
  Star,
  Clock,
  Send,
  Inbox,
  ChevronDown,
  ChevronUp,
  MessageCircle,
  Eye,
} from 'lucide-vue-next'


export default /*@__PURE__*/_defineComponent({
  __name: 'FeedbackSection',
  setup(__props) {

const authStore = useAuthStore()
const route = useRoute()
const { toast } = useToast()

// Form state
const feedbackType = ref<FeedbackType>('improvement')
const subject = ref('')
const description = ref('')
const rating = ref(0)
const hoveredStar = ref(0)
const isSubmitting = ref(false)
const isSubmitted = ref(false)
const submittedAsRating = ref(false)

// History state
const myFeedbacks = ref<UserFeedback[]>([])
const isLoadingHistory = ref(false)
const expandedFeedbackId = ref<string | null>(null)
const activeTab = ref('send')

const isRatingType = computed(() => feedbackType.value === 'rating')

const feedbackTypes = [
  { value: 'rating' as FeedbackType, label: 'Valoración', icon: Star, description: 'Califica tu experiencia' },
  { value: 'bug' as FeedbackType, label: 'Reportar Bug', icon: Bug, description: 'Algo no funciona correctamente' },
  { value: 'feature' as FeedbackType, label: 'Nueva Función', icon: Lightbulb, description: 'Sugerir una nueva funcionalidad' },
  { value: 'improvement' as FeedbackType, label: 'Mejora', icon: TrendingUp, description: 'Mejorar algo existente' },
  { value: 'question' as FeedbackType, label: 'Pregunta', icon: HelpCircle, description: 'Tengo una duda' },
]

const typeConfig: Record<FeedbackType, { icon: any; label: string; color: string }> = {
  bug: { icon: Bug, label: 'Bug', color: 'text-red-500 bg-red-500/10' },
  feature: { icon: Lightbulb, label: 'Feature', color: 'text-amber-500 bg-amber-500/10' },
  improvement: { icon: TrendingUp, label: 'Mejora', color: 'text-blue-500 bg-blue-500/10' },
  question: { icon: HelpCircle, label: 'Pregunta', color: 'text-violet-500 bg-violet-500/10' },
  rating: { icon: Star, label: 'Rating', color: 'text-yellow-500 bg-yellow-500/10' },
}

const statusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' }> = {
  new: { label: 'Enviado', variant: 'default' },
  reviewed: { label: 'Revisado', variant: 'secondary' },
  in_progress: { label: 'En progreso', variant: 'outline' },
  resolved: { label: 'Resuelto', variant: 'secondary' },
  wont_fix: { label: 'Cerrado', variant: 'destructive' },
}

const pendingCount = computed(() =>
  myFeedbacks.value.filter((f) => f.status === 'new' || f.status === 'in_progress').length
)

const withResponseCount = computed(() =>
  myFeedbacks.value.filter((f) => f.adminResponse || (f.responses && f.responses.length > 0)).length
)

// Fetch user's feedback history
const fetchMyFeedbacks = async () => {
  if (!authStore.userId) return
  isLoadingHistory.value = true
  try {
    const q = query(
      collection(db, 'userFeedback'),
      where('userId', '==', authStore.userId),
      orderBy('createdAt', 'desc')
    )
    const snapshot = await getDocs(q)
    myFeedbacks.value = snapshot.docs.map((d) => {
      const data = d.data()
      return {
        id: d.id,
        ...data,
        createdAt: data.createdAt?.toDate?.() || new Date(),
        updatedAt: data.updatedAt?.toDate?.() || new Date(),
        responses: (data.responses || []).map((r: any) => ({
          ...r,
          createdAt: r.createdAt?.toDate?.() || new Date(),
        })),
      } as UserFeedback
    })
  } catch (error) {
    console.error('Error fetching feedback history:', error)
  } finally {
    isLoadingHistory.value = false
  }
}

const handleSubmit = async () => {
  if (isRatingType.value) {
    if (rating.value === 0) {
      toast({
        title: 'Valoración requerida',
        description: 'Por favor selecciona una calificación de 1 a 5 estrellas',
        variant: 'destructive',
      })
      return
    }
  } else {
    if (!subject.value.trim() || !description.value.trim()) {
      toast({
        title: 'Campos requeridos',
        description: 'Por favor completa el asunto y la descripción',
        variant: 'destructive',
      })
      return
    }
  }

  isSubmitting.value = true

  try {
    const feedbackData: Record<string, unknown> = {
      tenantId: authStore.userTenantId || null,
      userId: authStore.userId || 'anonymous',
      userEmail: authStore.userEmail || 'anonymous',
      userRole: authStore.userRole || 'unknown',
      type: feedbackType.value,
      currentPage: route.fullPath,
      status: 'new',
      responses: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }

    if (isRatingType.value) {
      feedbackData.rating = rating.value
      feedbackData.subject = `Valoración: ${rating.value} estrellas`
      feedbackData.description = description.value.trim() || 'Sin comentarios adicionales'
    } else {
      feedbackData.subject = subject.value.trim()
      feedbackData.description = description.value.trim()
    }

    await addDoc(collection(db, 'userFeedback'), feedbackData)

    submittedAsRating.value = isRatingType.value
    isSubmitted.value = true
    toast({
      title: isRatingType.value ? 'Valoración enviada' : 'Feedback enviado',
      description: isRatingType.value
        ? 'Gracias por tu valoración. Tu opinión nos ayuda a mejorar.'
        : 'Gracias por tu comentario. Lo revisaremos pronto.',
    })

    // Refresh history
    fetchMyFeedbacks()
  } catch (error) {
    console.error('Error submitting feedback:', error)
    toast({
      title: 'Error',
      description: 'No se pudo enviar el feedback. Intenta de nuevo.',
      variant: 'destructive',
    })
  } finally {
    isSubmitting.value = false
  }
}

const resetForm = () => {
  isSubmitted.value = false
  submittedAsRating.value = false
  feedbackType.value = 'improvement'
  subject.value = ''
  description.value = ''
  rating.value = 0
  hoveredStar.value = 0
}

const toggleExpand = (id: string) => {
  expandedFeedbackId.value = expandedFeedbackId.value === id ? null : id
}

const getRatingText = (stars: number): string => {
  const texts = ['', 'Muy malo', 'Malo', 'Regular', 'Bueno', 'Excelente']
  return texts[stars] || ''
}

const renderStars = (n: number) => '★'.repeat(n) + '☆'.repeat(5 - n)

onMounted(fetchMyFeedbacks)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(_unref(MessageSquare), { class: "h-6 w-6 text-primary" }),
      _cache[8] || (_cache[8] = _createElementVNode("h2", { class: "text-2xl font-bold" }, "Feedback", -1))
    ]),
    _createVNode(_unref(Tabs), {
      modelValue: activeTab.value,
      "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((activeTab).value = $event)),
      class: "w-full"
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(TabsList), { class: "w-full mb-4" }, {
          default: _withCtx(() => [
            _createVNode(_unref(TabsTrigger), {
              value: "send",
              class: "flex-1 gap-1.5"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(Send), { class: "h-3.5 w-3.5" }),
                _cache[9] || (_cache[9] = _createTextVNode(" Enviar ", -1))
              ]),
              _: 1
            }),
            _createVNode(_unref(TabsTrigger), {
              value: "history",
              class: "flex-1 gap-1.5"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(Inbox), { class: "h-3.5 w-3.5" }),
                _cache[10] || (_cache[10] = _createTextVNode(" Mis envíos ", -1)),
                (myFeedbacks.value.length > 0)
                  ? (_openBlock(), _createBlock(_unref(Badge), {
                      key: 0,
                      variant: "secondary",
                      class: "text-[10px] px-1.5 py-0 ml-0.5"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(myFeedbacks.value.length), 1)
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            })
          ]),
          _: 1
        }),
        _createVNode(_unref(TabsContent), { value: "send" }, {
          default: _withCtx(() => [
            _cache[19] || (_cache[19] = _createElementVNode("p", { class: "text-muted-foreground text-sm mb-6" }, " Tu opinión nos ayuda a mejorar. Cuéntanos qué podemos hacer mejor. ", -1)),
            (isSubmitted.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                  _createElementVNode("div", _hoisted_3, [
                    _createVNode(_unref(CheckCircle), { class: "h-7 w-7 text-green-500" })
                  ]),
                  _createElementVNode("h3", _hoisted_4, _toDisplayString(submittedAsRating.value ? '¡Gracias por tu valoración!' : '¡Feedback enviado!'), 1),
                  _createElementVNode("p", _hoisted_5, _toDisplayString(submittedAsRating.value
              ? 'Tu opinión nos ayuda a mejorar continuamente.'
              : 'Lo revisaremos pronto. Puedes ver el estado en "Mis envíos".'), 1),
                  _createElementVNode("div", _hoisted_6, [
                    _createVNode(_unref(Button), {
                      variant: "outline",
                      size: "sm",
                      onClick: resetForm
                    }, {
                      default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                        _createTextVNode(" Enviar otro ", -1)
                      ]))]),
                      _: 1
                    }),
                    _createVNode(_unref(Button), {
                      variant: "ghost",
                      size: "sm",
                      onClick: _cache[0] || (_cache[0] = ($event: any) => (activeTab.value = 'history'))
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(Eye), { class: "mr-2 h-3.5 w-3.5" }),
                        _cache[12] || (_cache[12] = _createTextVNode(" Ver mis envíos ", -1))
                      ]),
                      _: 1
                    })
                  ])
                ]))
              : (_openBlock(), _createElementBlock("form", {
                  key: 1,
                  class: "space-y-5",
                  onSubmit: _withModifiers(handleSubmit, ["prevent"])
                }, [
                  _createElementVNode("div", _hoisted_7, [
                    _createVNode(_unref(Label), null, {
                      default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                        _createTextVNode("Tipo de feedback", -1)
                      ]))]),
                      _: 1
                    }),
                    _createVNode(_unref(RadioGroup), {
                      modelValue: feedbackType.value,
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((feedbackType).value = $event)),
                      class: "grid grid-cols-2 gap-2.5"
                    }, {
                      default: _withCtx(() => [
                        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(feedbackTypes, (type) => {
                          return _createElementVNode("div", {
                            key: type.value,
                            class: "relative"
                          }, [
                            _createVNode(_unref(RadioGroupItem), {
                              value: type.value,
                              id: type.value,
                              class: "peer sr-only"
                            }, null, 8, ["value", "id"]),
                            _createVNode(_unref(Label), {
                              for: type.value,
                              class: "flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 transition-colors"
                            }, {
                              default: _withCtx(() => [
                                (_openBlock(), _createBlock(_resolveDynamicComponent(type.icon), { class: "h-5 w-5 text-muted-foreground mt-0.5 shrink-0" })),
                                _createElementVNode("div", null, [
                                  _createElementVNode("p", _hoisted_8, _toDisplayString(type.label), 1),
                                  _createElementVNode("p", _hoisted_9, _toDisplayString(type.description), 1)
                                ])
                              ]),
                              _: 2
                            }, 1032, ["for"])
                          ])
                        }), 64))
                      ]),
                      _: 1
                    }, 8, ["modelValue"])
                  ]),
                  (isRatingType.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                        _createVNode(_unref(Label), null, {
                          default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                            _createTextVNode("¿Cómo calificarías tu experiencia?", -1)
                          ]))]),
                          _: 1
                        }),
                        _createElementVNode("div", _hoisted_11, [
                          _createElementVNode("div", _hoisted_12, [
                            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(5, (star) => {
                              return _createElementVNode("button", {
                                key: star,
                                type: "button",
                                class: "p-1 transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded",
                                onClick: ($event: any) => (rating.value = star),
                                onMouseenter: ($event: any) => (hoveredStar.value = star),
                                onMouseleave: _cache[2] || (_cache[2] = ($event: any) => (hoveredStar.value = 0))
                              }, [
                                _createVNode(_unref(Star), {
                                  class: _normalizeClass(["h-8 w-8 transition-colors", [
                      (hoveredStar.value || rating.value) >= star
                        ? 'text-yellow-400 fill-yellow-400'
                        : 'text-muted-foreground/30'
                    ]])
                                }, null, 8, ["class"])
                              ], 40, _hoisted_13)
                            }), 64))
                          ]),
                          _createElementVNode("p", {
                            class: _normalizeClass(["text-sm font-medium transition-opacity", hoveredStar.value || rating.value ? 'opacity-100' : 'opacity-0'])
                          }, _toDisplayString(getRatingText(hoveredStar.value || rating.value)), 3)
                        ]),
                        _createElementVNode("div", _hoisted_14, [
                          _createVNode(_unref(Label), { for: "rating-comment" }, {
                            default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                              _createTextVNode("Comentario (opcional)", -1)
                            ]))]),
                            _: 1
                          }),
                          _createVNode(_unref(Textarea), {
                            id: "rating-comment",
                            modelValue: description.value,
                            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((description).value = $event)),
                            placeholder: "¿Quieres agregar algún comentario sobre tu experiencia?",
                            rows: "3",
                            disabled: isSubmitting.value
                          }, null, 8, ["modelValue", "disabled"])
                        ])
                      ]))
                    : _createCommentVNode("", true),
                  (!isRatingType.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                        _createVNode(_unref(Label), { for: "subject" }, {
                          default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                            _createTextVNode("Asunto", -1)
                          ]))]),
                          _: 1
                        }),
                        _createVNode(_unref(Input), {
                          id: "subject",
                          modelValue: subject.value,
                          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((subject).value = $event)),
                          placeholder: "Resumen breve de tu feedback",
                          disabled: isSubmitting.value
                        }, null, 8, ["modelValue", "disabled"])
                      ]))
                    : _createCommentVNode("", true),
                  (!isRatingType.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                        _createVNode(_unref(Label), { for: "description" }, {
                          default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
                            _createTextVNode("Descripción", -1)
                          ]))]),
                          _: 1
                        }),
                        _createVNode(_unref(Textarea), {
                          id: "description",
                          modelValue: description.value,
                          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((description).value = $event)),
                          placeholder: "Describe con detalle tu sugerencia, problema o pregunta...",
                          rows: "4",
                          disabled: isSubmitting.value
                        }, null, 8, ["modelValue", "disabled"])
                      ]))
                    : _createCommentVNode("", true),
                  _createVNode(_unref(Button), {
                    type: "submit",
                    class: "w-full",
                    disabled: isSubmitting.value || (isRatingType.value ? rating.value === 0 : !subject.value.trim() || !description.value.trim())
                  }, {
                    default: _withCtx(() => [
                      (isSubmitting.value)
                        ? (_openBlock(), _createBlock(_unref(Loader2), {
                            key: 0,
                            class: "h-4 w-4 mr-2 animate-spin"
                          }))
                        : (_openBlock(), _createBlock(_unref(Send), {
                            key: 1,
                            class: "h-4 w-4 mr-2"
                          })),
                      _cache[18] || (_cache[18] = _createTextVNode(" Enviar Feedback ", -1))
                    ]),
                    _: 1
                  }, 8, ["disabled"])
                ], 32))
          ]),
          _: 1
        }),
        _createVNode(_unref(TabsContent), { value: "history" }, {
          default: _withCtx(() => [
            (isLoadingHistory.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                  (_openBlock(), _createElementBlock(_Fragment, null, _renderList(3, (i) => {
                    return _createVNode(_unref(Skeleton), {
                      key: i,
                      class: "h-20 w-full"
                    })
                  }), 64))
                ]))
              : (myFeedbacks.value.length === 0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                    _createVNode(_unref(Inbox), { class: "h-10 w-10 text-muted-foreground mx-auto mb-3" }),
                    _cache[21] || (_cache[21] = _createElementVNode("p", { class: "text-sm text-muted-foreground mb-4" }, "Aún no has enviado feedback.", -1)),
                    _createVNode(_unref(Button), {
                      variant: "outline",
                      size: "sm",
                      onClick: _cache[6] || (_cache[6] = ($event: any) => (activeTab.value = 'send'))
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(Send), { class: "mr-2 h-3.5 w-3.5" }),
                        _cache[20] || (_cache[20] = _createTextVNode(" Enviar mi primer feedback ", -1))
                      ]),
                      _: 1
                    })
                  ]))
                : (_openBlock(), _createElementBlock("div", _hoisted_19, [
                    _createElementVNode("div", _hoisted_20, [
                      _createElementVNode("span", null, _toDisplayString(myFeedbacks.value.length) + " enviados", 1),
                      (pendingCount.value > 0)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_21, _toDisplayString(pendingCount.value) + " pendientes", 1))
                        : _createCommentVNode("", true),
                      (withResponseCount.value > 0)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_22, _toDisplayString(withResponseCount.value) + " con respuesta", 1))
                        : _createCommentVNode("", true)
                    ]),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(myFeedbacks.value, (feedback) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: feedback.id,
                        class: _normalizeClass(["border rounded-lg overflow-hidden transition-colors", expandedFeedbackId.value === feedback.id ? 'border-primary/30 bg-primary/[0.02]' : ''])
                      }, [
                        _createElementVNode("button", {
                          class: "w-full flex items-start gap-3 p-3 text-left hover:bg-muted/50 transition-colors",
                          onClick: ($event: any) => (toggleExpand(feedback.id))
                        }, [
                          _createElementVNode("div", {
                            class: _normalizeClass(["h-8 w-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5", typeConfig[feedback.type]?.color])
                          }, [
                            (_openBlock(), _createBlock(_resolveDynamicComponent(typeConfig[feedback.type]?.icon), { class: "h-4 w-4" }))
                          ], 2),
                          _createElementVNode("div", _hoisted_24, [
                            _createElementVNode("div", _hoisted_25, [
                              _createElementVNode("p", _hoisted_26, _toDisplayString(feedback.type === 'rating' ? `${renderStars(feedback.rating || 0)}` : feedback.subject), 1)
                            ]),
                            _createElementVNode("div", _hoisted_27, [
                              _createVNode(_unref(Badge), {
                                variant: statusConfig[feedback.status]?.variant || 'outline',
                                class: "text-[10px] px-1.5 py-0"
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(statusConfig[feedback.status]?.label), 1)
                                ]),
                                _: 2
                              }, 1032, ["variant"]),
                              _createElementVNode("span", _hoisted_28, _toDisplayString(_unref(formatDate)(feedback.createdAt)), 1),
                              (feedback.adminResponse || (feedback.responses && feedback.responses.length > 0))
                                ? (_openBlock(), _createElementBlock("div", _hoisted_29, [
                                    _createVNode(_unref(MessageCircle), { class: "h-3 w-3" }),
                                    _cache[22] || (_cache[22] = _createTextVNode(" Respuesta ", -1))
                                  ]))
                                : _createCommentVNode("", true)
                            ])
                          ]),
                          (_openBlock(), _createBlock(_resolveDynamicComponent(expandedFeedbackId.value === feedback.id ? _unref(ChevronUp) : _unref(ChevronDown)), { class: "h-4 w-4 text-muted-foreground shrink-0 mt-1" }))
                        ], 8, _hoisted_23),
                        (expandedFeedbackId.value === feedback.id)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_30, [
                              _createVNode(_unref(Separator)),
                              (feedback.type === 'rating' && feedback.rating)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_31, [
                                    _createElementVNode("span", _hoisted_32, _toDisplayString(renderStars(feedback.rating)), 1),
                                    _createElementVNode("span", _hoisted_33, "(" + _toDisplayString(feedback.rating) + "/5)", 1)
                                  ]))
                                : _createCommentVNode("", true),
                              (feedback.description && feedback.description !== 'Sin comentarios adicionales')
                                ? (_openBlock(), _createElementBlock("div", _hoisted_34, [
                                    _cache[23] || (_cache[23] = _createElementVNode("p", { class: "text-xs text-muted-foreground mb-1" }, "Tu mensaje", -1)),
                                    _createElementVNode("p", _hoisted_35, _toDisplayString(feedback.description), 1)
                                  ]))
                                : _createCommentVNode("", true),
                              (feedback.currentPage)
                                ? (_openBlock(), _createElementBlock("p", _hoisted_36, [
                                    _cache[24] || (_cache[24] = _createTextVNode(" Enviado desde: ", -1)),
                                    _createElementVNode("span", _hoisted_37, _toDisplayString(feedback.currentPage), 1)
                                  ]))
                                : _createCommentVNode("", true),
                              (feedback.adminResponse)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_38, [
                                    _cache[25] || (_cache[25] = _createElementVNode("p", { class: "text-xs text-muted-foreground mb-1.5 font-medium" }, "Respuesta del equipo", -1)),
                                    _createElementVNode("div", _hoisted_39, [
                                      _createElementVNode("p", _hoisted_40, _toDisplayString(feedback.adminResponse), 1)
                                    ])
                                  ]))
                                : _createCommentVNode("", true),
                              (feedback.responses && feedback.responses.length > 0)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_41, [
                                    _createElementVNode("p", _hoisted_42, " Conversación (" + _toDisplayString(feedback.responses.length) + ") ", 1),
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(feedback.responses, (response) => {
                                      return (_openBlock(), _createElementBlock("div", {
                                        key: response.id,
                                        class: "rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3"
                                      }, [
                                        _createElementVNode("div", _hoisted_43, [
                                          _createElementVNode("span", _hoisted_44, _toDisplayString(response.respondedByEmail), 1),
                                          _createElementVNode("span", _hoisted_45, _toDisplayString(_unref(formatDateTime)(response.createdAt)), 1)
                                        ]),
                                        _createElementVNode("p", _hoisted_46, _toDisplayString(response.message), 1)
                                      ]))
                                    }), 128))
                                  ]))
                                : _createCommentVNode("", true),
                              (!feedback.adminResponse && (!feedback.responses || feedback.responses.length === 0))
                                ? (_openBlock(), _createElementBlock("div", _hoisted_47, [
                                    _createVNode(_unref(Clock), { class: "h-3.5 w-3.5" }),
                                    _cache[26] || (_cache[26] = _createTextVNode(" Pendiente de revisión por el equipo ", -1))
                                  ]))
                                : _createCommentVNode("", true)
                            ]))
                          : _createCommentVNode("", true)
                      ], 2))
                    }), 128))
                  ]))
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"])
  ]))
}
}

})